import type { PublicToInternalPropertyTransformerContext } from '@atlassian/ui-modifications-core/types';
import { FieldValueLookupError } from '@atlassian/ui-modifications-core/utils';
import { asColor } from '@atlassian/jira-issue-epic-color/src/common/utils.tsx';
import type { ParentFieldOption } from '@atlassian/jira-issue-field-parent/src/common/types.tsx';
import type {
	ParentGetPublicValue,
	ParentSetPublicValue,
} from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/parent.tsx';
import type {
	ParentIssueDetailsQuery_jira_issueById_fieldsById_edges_node_JiraIssueTypeField as JiraIssueTypeFieldNode,
	ParentIssueDetailsQuery_jira_issueById_fieldsById_edges_node_JiraSingleLineTextField as JiraSingleLineTextFieldNode,
	ParentIssueDetailsQuery_jira_issueById_fieldsById_edges_node_JiraStatusField as JiraStatusFieldNode,
} from './__generated_apollo__/ParentIssueDetailsQuery';
import {
	CAUSE_FETCH_PARENT_FIELD_DETAILS_MISSING_DATA,
	CAUSE_FETCH_PARENT_FIELD_NOT_FOUND,
	CAUSE_FETCH_PARENT_FIELD_UNKNOWN_ERROR,
} from './constants.tsx';
import {
	checkParentIssueIsDone,
	fetchParentIssueDetails,
	isJiraIssueTypeFieldNode,
	isJiraSingleLineTextFieldNode,
	isJiraStatusFieldNode,
} from './utils.tsx';

type ParentInternalValue = ParentFieldOption | null;

export async function parentSetValueTransformer(
	value: ParentSetPublicValue,
	_lookupValues: undefined,
	{ tenantContext }: PublicToInternalPropertyTransformerContext,
): Promise<ParentInternalValue> {
	if (value === null) {
		return null;
	}

	try {
		const parentIssueDetails = await fetchParentIssueDetails(tenantContext.cloudId, value);

		if (!parentIssueDetails) {
			throw new FieldValueLookupError(`Parent issue with issueId "${value}" not found`, {
				cause: CAUSE_FETCH_PARENT_FIELD_NOT_FOUND,
			});
		}

		const issue = parentIssueDetails.jira?.issueById;
		const fields = issue?.fieldsById?.edges;

		if (!issue || !issue.issueId || !issue.key || !fields) {
			throw new FieldValueLookupError(
				`Error fetching data for parent issue with issueId "${value}"`,
				{
					cause: CAUSE_FETCH_PARENT_FIELD_DETAILS_MISSING_DATA,
				},
			);
		}

		let issueTypeField: JiraIssueTypeFieldNode | null = null;
		let summaryField: JiraSingleLineTextFieldNode | null = null;
		let statusField: JiraStatusFieldNode | null = null;

		for (const field of fields) {
			if (isJiraIssueTypeFieldNode(field?.node)) {
				issueTypeField = field?.node ?? null;
			} else if (isJiraSingleLineTextFieldNode(field?.node)) {
				summaryField = field?.node ?? null;
			} else if (isJiraStatusFieldNode(field?.node)) {
				statusField = field?.node ?? null;
			}
		}

		if (!issueTypeField || !summaryField || !statusField) {
			throw new FieldValueLookupError(
				`Error fetching data for parent issue with issueId "${value}"`,
				{
					cause: CAUSE_FETCH_PARENT_FIELD_DETAILS_MISSING_DATA,
				},
			);
		}

		return {
			color:
				asColor(parentIssueDetails.jira.issueById.issueColorField?.color?.colorKey) ?? undefined,
			value: parentIssueDetails.jira.issueById.issueId,
			isDone: checkParentIssueIsDone(statusField),
			key: parentIssueDetails.jira.issueById.key,
			label: `${parentIssueDetails.jira.issueById.key} ${summaryField.text}`,
			fields: {
				summary: summaryField.text ?? '',
				issuetype: {
					id: issueTypeField.issueType?.issueTypeId ?? undefined,
					name: issueTypeField.issueType?.name ?? undefined,
					iconUrl: issueTypeField.issueType?.avatar?.xsmall ?? '',
				},
			},
		};
	} catch (error: unknown) {
		if (error instanceof Error && !('cause' in error)) {
			error.cause = CAUSE_FETCH_PARENT_FIELD_UNKNOWN_ERROR;
		}

		if (error instanceof Error) {
			throw new FieldValueLookupError(error.message, { cause: error.cause });
		}

		throw error;
	}
}

export function parentGetValueTransformer(
	intervalValue: ParentInternalValue,
): ParentGetPublicValue {
	if (!intervalValue) {
		return null;
	}

	return {
		id: intervalValue.value.toString(),
		key: intervalValue.key,
	};
}
